import React, { useRef, useCallback, useState } from "react";
import PropTypes from "prop-types";

/**
 * A reusable toggle switch input component with label and description.
 *
 * Renders a styled toggle switch (checkbox) with an associated label and
 * optional description text. The entire switch area is clickable.
 *
 * Supports both controlled mode (checked + onChange) and uncontrolled mode (defaultChecked).
 *
 * Base styles are in: base/common/_toggle_input.scss
 */
export const ToggleInput = ({
  id,
  name,
  checked,
  defaultChecked,
  onChange,
  label,
  description,
  required = false,
  className = "",
}) => {
  const inputRef = useRef(null);
  const isControlled = checked !== undefined;
  const [internalChecked, setInternalChecked] = useState(defaultChecked ?? false);
  const currentChecked = isControlled ? checked : internalChecked;

  const handleSwitchClick = useCallback((e) => {
    e.stopPropagation();
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (e) => {
      const newChecked = e.target.checked;
      if (!isControlled) {
        setInternalChecked(newChecked);
      }
      if (onChange) {
        onChange(newChecked);
      }
    },
    [isControlled, onChange],
  );

  return (
    <div className={`toggle-input ${className}`.trim()}>
      <span className="toggle-input-switch" onClick={handleSwitchClick}>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="checkbox"
          checked={currentChecked}
          onChange={handleChange}
          className="toggle-input-switch-input"
          required={required}
        />
        <span className="toggle-input-switch-slider" aria-hidden="true" />
      </span>
      <span className="toggle-input-content">
        <label className="toggle-input-content-label" htmlFor={id}>
          {label}
        </label>
        {description && (
          <span className="toggle-input-content-description">{description}</span>
        )}
      </span>
    </div>
  );
};

ToggleInput.propTypes = {
  /** Unique ID for the input element */
  id: PropTypes.string.isRequired,
  /** Name attribute for form submission */
  name: PropTypes.string,
  /** Whether the toggle is checked (controlled mode) */
  checked: PropTypes.bool,
  /** Initial checked state (uncontrolled mode) */
  defaultChecked: PropTypes.bool,
  /** Callback when the toggle state changes, receives the new checked value */
  onChange: PropTypes.func,
  /** Label text for the toggle */
  label: PropTypes.string.isRequired,
  /** Optional description text displayed below the label */
  description: PropTypes.string,
  /** Whether the input is required */
  required: PropTypes.bool,
  /** Additional CSS class name for the container */
  className: PropTypes.string,
};
